<template>
  <Collapser :collapserTitle="$t('insuranceProtectionTile.title')">
    <template #collapserContent>
      <DataBox>
        <template #databoxContent>
          <DataSection>
            <template #dataLines>
              <div class="data-line data-line__wrapper">
                <div class="data-line__term">
                  <span
                    v-dompurify-html="
                      $t('insuranceProtectionTile.tariff', {
                        tariff: tariff,
                        comprehension: comprehensionAsString,
                        overallCoverageRate: insuranceProtection!.sumInsuredFlatRate,
                        coverageRatePerPerson: insuranceProtection!.sumInsuredPerPerson
                      })
                    "
                  ></span>
                </div>
                <div class="data-line__definition">
                  {{ formatCurrency(insuranceProtection!.tariffAmount!) }}
                </div>
              </div>
              <div
                v-if="insuranceProtection?.passengerInsurance"
                class="data-line data-line__wrapper"
              >
                TEST2
                <div class="data-line__term">
                  <span>
                    {{ $t('insuranceProtectionTile.passengerInsurance.title') }}
                  </span>
                  <div v-if="showInsuranceDeathAmount">
                    <span>
                      {{
                        $t('insuranceProtectionTile.passengerInsurance.death', {
                          deathAmount: formatCurrency(
                            insuranceProtection!.passengerInsurance.claims
                              .insuredAmountDeath
                          )
                        })
                      }}
                    </span>
                  </div>
                  <div v-if="showInsuranceDisabilityAmount">
                    <span>
                      {{
                        $t('insuranceProtectionTile.passengerInsurance.disability', {
                          disabilityAmount: formatCurrency(
                            insuranceProtection!.passengerInsurance.claims
                              .insuredAmountDisability
                          )
                        })
                      }}
                    </span>
                  </div>
                </div>
                <div class="data-line__definition">
                  {{
                    formatCurrency(insuranceProtection!.passengerInsurance!.amount!) ||
                    $t('motorCoverage.notSelected')
                  }}
                </div>
              </div>
              <DataLine
                v-if="insuranceProtection?.assistanceCover"
                :label="$t('insuranceProtectionTile.assistanceCover')"
                :value="
                  formatCurrency(insuranceProtection!.assistanceCoverAmount!) ||
                  $t('motorCoverage.notSelected')
                "
              ></DataLine>
              <DataLine
                v-for="extra in insuranceProtection.motorCoverageLiabilityInsurance"
                :key="'motorCoverageLiabilityInsurance_' + extra.lex"
                :label="$t('motorCoverage.liabilityInsurance.' + extra.lex)"
                :value="formatCurrency(extra.amount!)"
              ></DataLine>
              <DataLine
                v-for="extra in insuranceProtection.additionalServiceLiabilityInsurance"
                :key="'additionalServicesLiabilityInsurance_' + extra.lex"
                :label="$t('motorCoverage.liabilityInsurance.' + extra.lex)"
                :value="$t('motorCoverage.notSelected')"
              ></DataLine>
              <template
                v-if="
                  insuranceProtection.comprehensiveInsuranceType ===
                  ComprehensiveInsuranceType.PARTIAL
                "
              >
                <DataLine
                  v-for="extra in insuranceProtection.motorCoveragePartiallyComprehensiveInsurance"
                  :key="'motorCoveragePartiallyComprehensiveInsurance_' + extra.lex"
                  :label="
                    $t('motorCoverage.partiallyComprehensiveInsurance.' + extra.lex)
                  "
                  :value="formatCurrency(extra.amount!)"
                ></DataLine>
                <DataLine
                  v-for="extra in insuranceProtection.additionalServicePartiallyComprehensiveInsurance"
                  :key="'additionalServicesPartiallyComprehensiveInsurance_' + extra.lex"
                  :label="
                    $t('motorCoverage.partiallyComprehensiveInsurance.' + extra.lex)
                  "
                  :value="$t('motorCoverage.notSelected')"
                ></DataLine>
              </template>
              <template
                v-if="
                  insuranceProtection.comprehensiveInsuranceType ===
                  ComprehensiveInsuranceType.FULL
                "
              >
                <DataLine
                  v-for="extra in insuranceProtection.motorCoverageFullyComprehensiveInsurance"
                  :key="'motorCoverageFullyComprehensiveInsurance_' + extra.lex"
                  :label="$t('motorCoverage.fullyComprehensiveInsurance.' + extra.lex)"
                  :value="formatCurrency(extra.amount!)"
                ></DataLine>
                <DataLine
                  v-for="extra in insuranceProtection.additionalServiceFullyComprehensiveInsurance"
                  :key="'additionalServicesFullyComprehensiveInsurance_' + extra.lex"
                  :label="$t('motorCoverage.fullyComprehensiveInsurance.' + extra.lex)"
                  :value="$t('motorCoverage.notSelected')"
                ></DataLine>
              </template>
              <DataLine
                :label="$t('insuranceProtectionTile.paymentPeriod')"
                :value="$t('paymentPeriod.' + insuranceProtection!.paymentPeriod)"
              ></DataLine>
              <DataLine
                :label="$t('insuranceProtectionTile.overallAmount')"
                :value="formatCurrency(insuranceProtection!.overallAmount!)"
              ></DataLine>
            </template>
          </DataSection>
        </template>
      </DataBox>
    </template>
  </Collapser>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue'
import { Collapser, DataBox, DataLine, DataSection } from 'de-zurich-ui-library'
import { formatCurrency } from '@/utils/motor/MotorDataBoxDataUtils'
import {
  ComprehensiveInsuranceType,
  type InsuranceProtection
} from '@/typesTenant/NewVehicleDetailApi'

const props = defineProps({
  insuranceProtection: {
    type: Object as PropType<InsuranceProtection>,
    required: true
  },
  tariff: {
    type: String,
    required: true
  },
  comprehensionAsString: {
    type: String,
    required: true
  }
})

// const ComprehensiveInsuranceType = ComprehensiveInsuranceType

const showInsuranceDeathAmount = computed(() => {
  return (
    props.insuranceProtection.passengerInsurance?.claims?.insuredAmountDeath?.amount !== 0
  )
})

const showInsuranceDisabilityAmount = computed(() => {
  return (
    props.insuranceProtection.passengerInsurance?.claims?.insuredAmountDisability
      ?.amount !== 0
  )
})
</script>
